import { useEffect, useRef, useState } from 'react';
import type { Locale } from '../i18n/locales';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

interface BuildCardProps {
  locale: Locale;
}

const PROGRESS_CENTER = 68;
const PROGRESS_AMPLITUDE = 8;
const PROGRESS_EASE = 0.08;

export function BuildCard({ locale }: BuildCardProps): JSX.Element {
  const [pct, setPct] = useState(0);
  const rafRef = useRef<number | null>(null);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (reducedMotion) {
      setPct(PROGRESS_CENTER);
      return;
    }

    let current = 0;
    const tick = (): void => {
      const target = PROGRESS_CENTER + Math.sin(Date.now() / 1800) * PROGRESS_AMPLITUDE;
      current += (target - current) * PROGRESS_EASE;
      setPct(current);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [reducedMotion]);

  const pctRounded = Math.round(pct);

  return (
    <div className="build-card" role="status" aria-live="polite">
      <div className="build-card__head">
        <span className="build-card__dot" style={{ background: '#FF5F57' }} aria-hidden="true" />
        <span className="build-card__dot" style={{ background: '#FEBC2E' }} aria-hidden="true" />
        <span className="build-card__dot" style={{ background: '#28C840' }} aria-hidden="true" />
        <span className="build-card__file">dsoftstudio.com · build</span>
      </div>
      <div className="build-card__body">
        <div className="build-step">
          <span className="build-step__icon build-step__icon--ok" aria-hidden="true">
            ✓
          </span>
          <span className="build-step__name">{locale.steps.brand}</span>
          <span className="build-step__pct">100%</span>
        </div>
        <div className="build-step">
          <span className="build-step__icon build-step__icon--ok" aria-hidden="true">
            ✓
          </span>
          <span className="build-step__name">{locale.steps.arch}</span>
          <span className="build-step__pct">100%</span>
        </div>
        <div className="build-step is-active">
          <span className="build-step__icon build-step__icon--run" aria-hidden="true">
            ·
          </span>
          <span className="build-step__name">{locale.steps.fe}</span>
          <span className="build-step__pct">{pctRounded}%</span>
        </div>
        <div className="progress-row">
          <div
            className="progress"
            role="progressbar"
            aria-valuenow={pctRounded}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={locale.steps.fe}
          >
            <div className="progress__fill" style={{ width: `${pct.toFixed(1)}%` }} />
          </div>
        </div>
        <div className="build-step">
          <span className="build-step__icon build-step__icon--idle" aria-hidden="true">
            ○
          </span>
          <span className="build-step__name">{locale.steps.qa}</span>
          <span className="build-step__pct">{locale.steps.pending}</span>
        </div>
        <div className="build-step">
          <span className="build-step__icon build-step__icon--idle" aria-hidden="true">
            ○
          </span>
          <span className="build-step__name">{locale.steps.launch}</span>
          <span className="build-step__pct">{locale.steps.eta}</span>
        </div>
      </div>
    </div>
  );
}
